/**
 * Fehlerpaket, erste Scheibe: ein Satz, Kategorie Problem, ohne Freitext.
 * Klasse PORTAL_FEHLERPAKET — fail-closed. Unsicherer Inhalt wird nicht gespeichert.
 * Kein Screenshot, kein Bild, kein Auto-Fix.
 */

const CODE_TOKEN_RE = /^[A-Za-z][A-Za-z0-9_]{1,79}$/;
const BUILD_TOKEN_RE = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,79}$/;
const REQUEST_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,120}$/;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const PHONE_RE = /(?:\+|00)\d{1,3}[\s./-]?\d(?:[\d\s./()-]{5,}\d)|\b0\d{2,4}[\s./-]?\d{3,}[\s./-]?\d{2,}\b/;
const FILENAME_RE = /\b[\w.-]{1,80}\.(?:pdf|docx?|txt|rtf|odt|xlsx?|png|jpe?g|webp|gif|csv|zip)\b/i;
const FILE_EXT_RE = /\b[\w.-]+\.[A-Za-z]{2,5}\b/;
const PROSE_RE = /\b(?:akte|akten|chat|mandant|klient|person|herr|frau)\b/i;
const SECRETISH_RE = /bearer|token|secret|password|eyJ/i;
const TECHNICAL_LINE_RE = /\b(?:Error|TypeError|ReferenceError|SyntaxError|RangeError|HTTP|status|failed|fail|ECONN|ETIMEDOUT|AbortError|Unhandled|WARN|GET|POST|PUT|PATCH|DELETE)\b|^[A-Za-z][A-Za-z0-9_.:/-]{1,79}$/;
const INDEX_CHUNK_RE = /(?:^|\/)(index-[A-Za-z0-9_-]{4,32}\.js)(?:\?|#|$)/;
const QUALITY_GATE_RE = /quality_gate/i;

const MAX_CONSOLE_LINES = 6;
const MAX_CONSOLE_CHARS = 160;

export interface PortalFehlerpaketDiag {
  console_lines?: string[];
  last_diag_step?: string;
  dist_fingerprint?: string;
}

export interface PortalFehlerpaketPayload {
  kategorie: 'Problem';
  nachricht: string;
  request_id?: string;
  diag: PortalFehlerpaketDiag;
  /** Fehlt die Zahl, gilt 1. Ab der zweiten gleichen Signatur steht sie am bestehenden Paket. */
  wiederholungen?: number;
}

export interface PortalFehlerpaketInput {
  requestId?: string;
  request_id?: string;
  consoleLines?: string[];
  console_lines?: string[];
  pipeline?: Array<{ step?: string }>;
  workflowStep?: string;
  workflow_step?: string;
  lastDiagStep?: string;
  last_diag_step?: string;
  qualityGate?: string;
  quality_gate?: string;
  portalBuild?: string;
  portal_build?: string;
  indexChunk?: string;
  distFingerprint?: string;
  dist_fingerprint?: string;
  screenshot?: unknown;
  image?: unknown;
}

interface DiagMemory {
  requestId: string;
  lastDiagStep: string;
  qualityGate: string;
}

const diagMemory: DiagMemory = { requestId: '', lastDiagStep: '', qualityGate: '' };

function clip(value: unknown, max: number): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
}

export function sanitizeCodeToken(raw: unknown, max = 80): string {
  const value = clip(raw, max);
  if (!value || !CODE_TOKEN_RE.test(value)) return '';
  return value;
}

function isQualityGateName(name: string, qualityGate: string): boolean {
  if (!name) return true;
  if (QUALITY_GATE_RE.test(name)) return true;
  if (qualityGate && name.toLowerCase() === qualityGate.toLowerCase()) return true;
  return false;
}

export function sanitizeRequestId(raw: unknown): string {
  const value = clip(raw, 120);
  if (!value || !REQUEST_ID_RE.test(value)) return '';
  if (EMAIL_RE.test(value) || PHONE_RE.test(value) || FILENAME_RE.test(value) || SECRETISH_RE.test(value)) {
    return '';
  }
  if (/\s/.test(value)) return '';
  return value;
}

export function sanitizeConsoleLines(lines: unknown): string[] {
  if (!Array.isArray(lines)) return [];
  const out: string[] = [];
  for (const raw of lines) {
    if (typeof raw !== 'string' && !(raw instanceof Error)) continue;
    const line = raw instanceof Error ? clip(`${raw.name}: ${raw.message}`, 400) : clip(raw, 400);
    if (!line || line.length > MAX_CONSOLE_CHARS) continue;
    if (EMAIL_RE.test(line) || PHONE_RE.test(line)) continue;
    if (FILENAME_RE.test(line) || FILE_EXT_RE.test(line)) continue;
    if (PROSE_RE.test(line) || SECRETISH_RE.test(line)) continue;
    if (!TECHNICAL_LINE_RE.test(line)) continue;
    if (line.split(' ').length > 16) continue;
    out.push(line);
    if (out.length >= MAX_CONSOLE_LINES) break;
  }
  return out;
}

export function sanitizeDistFingerprint(raw: unknown): string {
  const value = clip(raw, 160);
  if (!value || EMAIL_RE.test(value) || PHONE_RE.test(value) || SECRETISH_RE.test(value)) return '';
  const chunk = value.match(INDEX_CHUNK_RE);
  if (chunk?.[1] && BUILD_TOKEN_RE.test(chunk[1])) return chunk[1];
  if (value.length <= 80 && BUILD_TOKEN_RE.test(value) && !FILENAME_RE.test(value)) return value;
  return '';
}

export function readDistFingerprint(env?: { portalBuild?: string; scriptSrcs?: string[] }): string {
  const fromBuild = sanitizeDistFingerprint(env?.portalBuild);
  if (fromBuild) return fromBuild;
  for (const src of env?.scriptSrcs ?? []) {
    const chunk = sanitizeDistFingerprint(src);
    if (chunk.startsWith('index-')) return chunk;
  }
  return '';
}

export function pickLastDiagStep(
  steps: Array<{ step?: string }> | undefined,
  qualityGate: string,
): string {
  const list = Array.isArray(steps) ? steps : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const name = sanitizeCodeToken(list[i]?.step);
    if (!name || isQualityGateName(name, qualityGate)) continue;
    return name;
  }
  return '';
}

export function resolveLastDiagStep(input: PortalFehlerpaketInput): string {
  const quality = sanitizeCodeToken(input.qualityGate || input.quality_gate);
  const explicit = sanitizeCodeToken(input.lastDiagStep || input.last_diag_step);
  if (explicit && !isQualityGateName(explicit, quality)) return explicit;
  const fromPipeline = pickLastDiagStep(input.pipeline, quality);
  if (fromPipeline) return fromPipeline;
  const workflow = sanitizeCodeToken(input.workflowStep || input.workflow_step);
  if (workflow && !isQualityGateName(workflow, quality)) return workflow;
  return '';
}

export function applyFehlerpaketDiagFields(
  out: PortalFehlerpaketDiag,
  meta: PortalFehlerpaketInput,
): void {
  const lines = sanitizeConsoleLines(meta.console_lines ?? meta.consoleLines);
  if (lines.length > 0) out.console_lines = lines;
  const step = resolveLastDiagStep(meta);
  if (step) out.last_diag_step = step;
  const dist = sanitizeDistFingerprint(meta.dist_fingerprint || meta.distFingerprint)
    || sanitizeDistFingerprint(meta.portal_build || meta.portalBuild);
  if (dist) out.dist_fingerprint = dist;
}

export function buildPortalFehlerpaketPayload(input: PortalFehlerpaketInput): PortalFehlerpaketPayload {
  const requestId = sanitizeRequestId(input.requestId ?? input.request_id);
  const diag: PortalFehlerpaketDiag = {};
  applyFehlerpaketDiagFields(diag, {
    ...input,
    consoleLines: input.consoleLines ?? input.console_lines,
    portalBuild: input.portalBuild ?? input.portal_build,
    distFingerprint: input.distFingerprint
      ?? input.dist_fingerprint
      ?? readDistFingerprint({
        portalBuild: input.portalBuild ?? input.portal_build,
        scriptSrcs: input.indexChunk ? [input.indexChunk] : [],
      }),
  });
  const bits = ['Sichtbarer Fehler.'];
  if (requestId) bits.push(`request_id=${requestId}.`);
  if (diag.last_diag_step) bits.push(`letzter_diag=${diag.last_diag_step}.`);
  if (diag.dist_fingerprint) bits.push(`dist=${diag.dist_fingerprint}.`);
  if (diag.console_lines?.length) bits.push(`konsole=${diag.console_lines.join(' | ')}.`);
  const payload: PortalFehlerpaketPayload = {
    kategorie: 'Problem',
    nachricht: bits.join(' ').slice(0, 1000),
    diag,
  };
  if (requestId) payload.request_id = requestId;
  return payload;
}

export function notePortalDiagContext(input: PortalFehlerpaketInput): void {
  const step = resolveLastDiagStep(input);
  const requestId = sanitizeRequestId(input.requestId ?? input.request_id);
  const quality = sanitizeCodeToken(input.qualityGate || input.quality_gate);
  if (step) diagMemory.lastDiagStep = step;
  if (requestId) diagMemory.requestId = requestId;
  if (quality) diagMemory.qualityGate = quality;
}

export function readPortalDiagContext(): { requestId: string; lastDiagStep: string; qualityGate: string } {
  return { ...diagMemory };
}

export function resetPortalFehlerpaketMemoryForTests(): void {
  diagMemory.requestId = '';
  diagMemory.lastDiagStep = '';
  diagMemory.qualityGate = '';
}

/**
 * Gemeinsamer Ausgang der Portal-Aufrufe. Klasse PORTAL_FEHLERPAKET, fail-closed.
 * Genannte HTTP-Status und ein Aufruf ohne Status schreiben ein Paket.
 * Ein 200 schreibt keines. Kein zweiter Sammler, kein Antworttext in der Signatur.
 */
const ACCEPTED_HTTP_STATUS = new Set([400, 401, 403, 404, 408, 409, 422, 429, 500, 502, 503, 504]);

export interface PortalFehlerausgangInput extends PortalFehlerpaketInput {
  status?: number;
}

export type PortalFehlerausgangWriter = (payload: PortalFehlerpaketPayload) => void;

const exitLog: PortalFehlerpaketPayload[] = [];
const exitBySignature = new Map<string, PortalFehlerpaketPayload>();
let exitWriter: PortalFehlerausgangWriter | null = null;

const PACKAGE_KEYS = new Set(['kategorie', 'nachricht', 'request_id', 'diag', 'wiederholungen']);
const DIAG_KEYS = new Set(['console_lines', 'last_diag_step', 'dist_fingerprint']);

export function setPortalFehlerausgangWriter(next: PortalFehlerausgangWriter | null): void {
  exitWriter = next;
}

export function readPortalFehlerausgangLog(): PortalFehlerpaketPayload[] {
  return exitLog.slice();
}

export function resetPortalFehlerausgangForTests(): void {
  exitLog.length = 0;
  exitBySignature.clear();
  exitWriter = null;
  resetPortalFehlerpaketMemoryForTests();
}

function packageWithoutSurfaceList(payload: PortalFehlerpaketPayload): PortalFehlerpaketPayload {
  const diag: PortalFehlerpaketDiag = {};
  if (payload.diag.console_lines?.length) diag.console_lines = payload.diag.console_lines;
  if (payload.diag.last_diag_step) diag.last_diag_step = payload.diag.last_diag_step;
  if (payload.diag.dist_fingerprint) diag.dist_fingerprint = payload.diag.dist_fingerprint;
  const out: PortalFehlerpaketPayload = {
    kategorie: 'Problem',
    nachricht: payload.nachricht,
    diag,
  };
  if (payload.request_id) out.request_id = payload.request_id;
  if (typeof payload.wiederholungen === 'number' && payload.wiederholungen >= 2) {
    out.wiederholungen = payload.wiederholungen;
  }
  for (const key of Object.keys(out)) {
    if (!PACKAGE_KEYS.has(key)) delete (out as unknown as Record<string, unknown>)[key];
  }
  for (const key of Object.keys(out.diag)) {
    if (!DIAG_KEYS.has(key)) delete (out.diag as unknown as Record<string, unknown>)[key];
  }
  return out;
}

function acceptedStatus(status: number | undefined): boolean {
  if (status == null) return true;
  return Number.isInteger(status) && ACCEPTED_HTTP_STATUS.has(status);
}

/** Signatur aus Status und kurzer technischer Zeile. Der Antworttext zählt nicht mit. */
function signatureOf(input: PortalFehlerausgangInput): { key: string; line: string } | null {
  if (!acceptedStatus(input.status)) return null;
  const safe = sanitizeConsoleLines(input.consoleLines ?? input.console_lines);
  if (typeof input.status === 'number') {
    const canonical = `HTTP ${input.status}`;
    const line = safe.includes(canonical) ? canonical : (safe[0] || canonical);
    return { key: `${input.status}|${line}`, line };
  }
  const line = safe[0] || 'Netz';
  return { key: `netz|${line}`, line };
}

function consoleLinesForExit(input: PortalFehlerausgangInput, line: string): string[] {
  const safe = sanitizeConsoleLines(input.consoleLines ?? input.console_lines).filter((item) => item !== line);
  return [line, ...safe].slice(0, MAX_CONSOLE_LINES);
}

function rememberExit(signature: string, payload: PortalFehlerpaketPayload): void {
  exitBySignature.set(signature, payload);
  exitLog.push(payload);
  if (exitLog.length > 8) {
    const dropped = exitLog.shift();
    if (!dropped) return;
    for (const [key, value] of exitBySignature) {
      if (value === dropped) exitBySignature.delete(key);
    }
  }
}

/**
 * Kurze Laufzeitzeile ohne Personeninhalt. Unsicheres bleibt ein festes Wort.
 */
export function portalLaufzeitZumAusgang(
  kind: 'error' | 'unhandledrejection',
  raw: unknown,
): PortalFehlerpaketPayload | null {
  const text = raw instanceof Error
    ? `${raw.name}: ${raw.message}`
    : typeof raw === 'string'
      ? raw
      : '';
  const safe = sanitizeConsoleLines([text]);
  const line = safe[0] || (kind === 'unhandledrejection' ? 'unhandledrejection' : 'Error');
  return portalFehlerausgang({ consoleLines: [line] });
}

/** Genannte Status und Netz durch diesen Ausgang. Ein 200 schreibt kein Paket. */
export function portalFehlerausgang(input: PortalFehlerausgangInput): PortalFehlerpaketPayload | null {
  const signature = signatureOf(input);
  if (!signature) return null;
  const existing = exitBySignature.get(signature.key);
  if (existing) {
    existing.wiederholungen = (existing.wiederholungen ?? 1) + 1;
    return existing;
  }
  const payload = packageWithoutSurfaceList(buildPortalFehlerpaketPayload({
    ...input,
    consoleLines: consoleLinesForExit(input, signature.line),
  }));
  rememberExit(signature.key, payload);
  try {
    exitWriter?.(payload);
  } catch {
    /* Der Ausgang darf die sichtbare Meldung nicht umwerfen. */
  }
  return payload;
}
